import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box } from '@mui/material';
import { Form, formService, ParameterChangeDto } from '../../services/formService';
import { Appointment } from '../../services/appointmentService';
import { UserDataFieldDto } from '../../types/customUserDataFields';
import InteractivePDFViewer from './InteractivePDFViewer';
import FormParameterChangesAlert from '../forms/FormParameterChangesAlert';
import { extractApiErrorMessage } from '../../utils/apiUtils';
import {
  handleSavePixelVersion,
  handleDownloadAndSavePixelVersion,
  handleSaveFormDataOnly
} from '../../services/pixelVersionPdfService';

export interface PixelVersionPDFViewerProps {
  pdfBytes: Uint8Array | null;
  loading: boolean;
  form: Form;
  job: any;
  customer: any;
  appointments: Appointment[];
  loadFormResponses: (formId: number, updateState?: boolean, forceRefresh?: boolean) => Promise<any>;
  generatePixelVersionPdf: (form: Form, silent?: boolean) => Promise<void>;
  setSuccess: (message: string | null) => void;
  setError: (message: string | null) => void;
  setSkipPdfRegeneration: (skip: boolean) => void;
  onRetry?: () => void;
  userDataFields?: UserDataFieldDto[];
  initialFormData?: { [key: string]: any };
  onDocumentSaved?: () => void;
  onApplyParameterChanges?: (updates: { [fieldCode: string]: any }) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  /** Multi-instance forms: which copy this viewer is showing. Defaults to 1 (single-instance behavior). */
  instanceIndex?: number;
  instanceLabel?: string | null;
  /** Whether a saved instance row exists (drives the per-instance menu on the toolbar). */
  instanceExists?: boolean;
  /** Open the "add a copy" dialog for this form. */
  onNewFormInstance?: (form: Form) => void;
  /** Open the per-instance overflow (rename/delete) menu, anchored to the kebab. */
  onOpenInstanceMenu?: (anchorEl: HTMLElement, form: Form, instanceIndex: number, label: string | null) => void;
}

const PixelVersionPDFViewer: React.FC<PixelVersionPDFViewerProps> = ({
  pdfBytes,
  loading,
  form,
  job,
  customer,
  appointments,
  loadFormResponses,
  generatePixelVersionPdf,
  setSuccess,
  setError,
  setSkipPdfRegeneration,
  onRetry,
  userDataFields,
  initialFormData,
  onDocumentSaved,
  onApplyParameterChanges,
  onDirtyChange,
  instanceIndex = 1,
  instanceLabel = null,
  instanceExists = false,
  onNewFormInstance,
  onOpenInstanceMenu
}) => {
  const [parameterChanges, setParameterChanges] = useState<ParameterChangeDto[]>([]);
  const [changesDismissed, setChangesDismissed] = useState(false);
  const [hasUnsavedAppliedChanges, setHasUnsavedAppliedChanges] = useState(false);

  // Track field codes that have been applied (but not yet saved to DB)
  // Using a ref so it doesn't trigger re-renders
  const appliedFieldCodesRef = useRef<Set<string>>(new Set());

  // Track initial fetch to avoid re-fetching after applying changes
  const hasFetchedRef = useRef(false);

  // Fetch parameter changes only once when form is first loaded with saved data
  useEffect(() => {
    const fetchParameterChanges = async () => {
      if (!job?.id || !form?.id || !initialFormData || Object.keys(initialFormData).length === 0) {
        setParameterChanges([]);
        return;
      }

      // Only fetch once per form load, not on every initialFormData change
      if (hasFetchedRef.current) {
        return;
      }
      hasFetchedRef.current = true;

      try {
        const response = await formService.getParameterChanges(job.id, form.id, instanceIndex);
        // Filter out any changes that have already been applied
        const filteredChanges = (response.changes || []).filter(
          c => !appliedFieldCodesRef.current.has(c.fieldCode)
        );
        setParameterChanges(filteredChanges);
        setChangesDismissed(false);
      } catch (error) {
        console.error('[PixelVersionPDFViewer] Failed to fetch parameter changes:', error);
        setParameterChanges([]);
      }
    };

    fetchParameterChanges();
  }, [job?.id, form?.id, instanceIndex, initialFormData]);

  // Reset the fetch flag when the form or instance changes so each copy fetches its own changes.
  useEffect(() => {
    hasFetchedRef.current = false;
    appliedFieldCodesRef.current.clear();
  }, [form?.id, instanceIndex]);

  const handleApplyChange = useCallback((fieldCode: string, newValue: string) => {
    // Track this field as applied so it doesn't reappear after PDF regeneration
    appliedFieldCodesRef.current.add(fieldCode);

    if (onApplyParameterChanges) {
      // Parse JSON arrays if needed
      let parsedValue: any = newValue;
      if (newValue && newValue.startsWith('[') && newValue.endsWith(']')) {
        try {
          parsedValue = JSON.parse(newValue);
        } catch {
          // Keep as string if not valid JSON
        }
      }
      onApplyParameterChanges({ [fieldCode]: parsedValue });
    }
    setHasUnsavedAppliedChanges(true);
    // Remove the applied change from the list
    setParameterChanges(prev => prev.filter(c => c.fieldCode !== fieldCode));
  }, [onApplyParameterChanges]);

  const handleApplyAll = useCallback(() => {
    // Track all fields as applied so they don't reappear after PDF regeneration
    parameterChanges.forEach(change => {
      appliedFieldCodesRef.current.add(change.fieldCode);
    });

    if (onApplyParameterChanges) {
      const updates: { [fieldCode: string]: any } = {};
      parameterChanges.forEach(change => {
        let parsedValue: any = change.newValue;
        if (change.newValue && change.newValue.startsWith('[') && change.newValue.endsWith(']')) {
          try {
            parsedValue = JSON.parse(change.newValue);
          } catch {
            // Keep as string if not valid JSON
          }
        }
        updates[change.fieldCode] = parsedValue;
      });
      onApplyParameterChanges(updates);
    }
    setHasUnsavedAppliedChanges(true);
    setParameterChanges([]);
  }, [onApplyParameterChanges, parameterChanges]);

  const handleDismissChanges = useCallback(() => {
    setChangesDismissed(true);
  }, []);

  const handleRetryFromParent = useCallback(async () => {
    setError(null);
    await generatePixelVersionPdf(form);
  }, [generatePixelVersionPdf, form]);

  // Auto-save handler: saves form data only (no PDF generation)
  const handleAutoSave = useCallback(async (capturedFormData: { [key: string]: any }) => {
    if (!job?.id) return;

    try {
      await handleSaveFormDataOnly(form, job.id, capturedFormData, loadFormResponses, instanceIndex);
      // Clear the flag after successful save so future initialFormData changes
      // (e.g., from API refreshes) correctly reset the baseline again
      setHasUnsavedAppliedChanges(false);
    } catch (err: any) {
      console.error('[PixelVersionPDFViewer] Auto-save failed:', err);
      throw err; // Re-throw so useAutoSave can track the error
    }
  }, [form, job?.id, loadFormResponses, instanceIndex]);

  const handleDownloadAndSave = useCallback(async (capturedFormData: any, saveOnly = false, shouldPrint = false): Promise<void> => {
    try {
      // Prevent automatic PDF regeneration during these operations
      setSkipPdfRegeneration(true);

      setError(null);
      if (saveOnly) {
        await handleSavePixelVersion(form, job, customer, appointments, loadFormResponses, capturedFormData, userDataFields, onDocumentSaved, instanceIndex, instanceLabel);
        setHasUnsavedAppliedChanges(false);
        setSuccess('Form data saved successfully');
      } else {
        const successMessage = await handleDownloadAndSavePixelVersion(
          form,
          job,
          customer,
          appointments,
          loadFormResponses,
          capturedFormData,
          shouldPrint,
          userDataFields,
          instanceIndex,
          instanceLabel
        );
        setHasUnsavedAppliedChanges(false);
        setSuccess(successMessage || (shouldPrint ? 'Print dialog opened' : 'PDF downloaded successfully'));
      }
    } catch (err: any) {
      setError(extractApiErrorMessage(err, 'Failed to save/download form'));
      throw err; // Re-throw so the loading state can be cleared
    } finally {
      setSkipPdfRegeneration(false);
    }
  }, [form, job, customer, appointments, loadFormResponses, userDataFields, onDocumentSaved, setSkipPdfRegeneration, setError, setSuccess, instanceIndex, instanceLabel]);

  return (
    <Box data-testid="pixel-pdf-viewer">
      {!changesDismissed && parameterChanges.length > 0 && (
        <FormParameterChangesAlert
          changes={parameterChanges}
          onApplyChange={handleApplyChange}
          onApplyAll={handleApplyAll}
          onDismiss={handleDismissChanges}
        />
      )}
      <InteractivePDFViewer
        pdfBytes={pdfBytes}
        loading={loading}
        form={form}
        userDataFields={userDataFields}
        onDownloadAndSave={handleDownloadAndSave}
        onRetry={handleRetryFromParent}
        initialFormData={initialFormData}
        onDirtyChange={onDirtyChange}
        onAutoSave={handleAutoSave}
        autoSaveEnabled={true}
        hasUnsavedAppliedChanges={hasUnsavedAppliedChanges}
        instanceIndex={instanceIndex}
        instanceLabel={instanceLabel}
        instanceExists={instanceExists}
        onNewFormInstance={onNewFormInstance}
        onOpenInstanceMenu={onOpenInstanceMenu}
      />
    </Box>
  );
};

const MemoizedPixelVersionPDFViewer = React.memo(PixelVersionPDFViewer, (prevProps, nextProps) => {
  // Only re-render when rendering-relevant props change
  // Exclude callback props since they don't affect render output
  return (
    prevProps.pdfBytes === nextProps.pdfBytes &&
    prevProps.loading === nextProps.loading &&
    prevProps.form.id === nextProps.form.id &&
    prevProps.initialFormData === nextProps.initialFormData &&
    prevProps.userDataFields === nextProps.userDataFields &&
    prevProps.instanceIndex === nextProps.instanceIndex &&
    prevProps.instanceLabel === nextProps.instanceLabel &&
    prevProps.instanceExists === nextProps.instanceExists
  );
});
export default MemoizedPixelVersionPDFViewer;
